import { Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// --- Types ---

export interface CollectionFilter {
  id: string;
  fieldName: string;
  operator: string;
  value: string;
}

export interface CollectionSort {
  fieldName: string;
  order: "asc" | "desc";
}

export interface CollectionItemsConfig {
  mode: "dynamic" | "selected";
  filters: CollectionFilter[];
  sort: CollectionSort | null;
  selectedItemIds: string[];
}

interface FieldDef {
  name: string;
  displayName?: string;
  fieldType?: string;
  isRequired?: boolean;
}

interface CollectionItemsPanelProps {
  config: CollectionItemsConfig;
  fields: FieldDef[];
  allItems: Array<Record<string, unknown>>;
  onChange: (config: CollectionItemsConfig) => void;
  compact?: boolean;
}

// --- Operator helpers ---

const TEXT_OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "does_not_equal", label: "Does not equal" },
  { value: "contains", label: "Contains" },
  { value: "does_not_contain", label: "Does not contain" },
];

const DATE_OPERATORS = [
  { value: "is_set", label: "Is set" },
  { value: "is_not_set", label: "Is not set" },
  { value: "is_after_or_equal", label: "Is after or equal to..." },
  { value: "is_before_or_equal", label: "Is before or equal to..." },
];

const NUMBER_OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "does_not_equal", label: "Does not equal" },
  { value: "is_greater_than", label: "Is greater than" },
  { value: "is_less_than", label: "Is less than" },
  { value: "is_set", label: "Is set" },
  { value: "is_not_set", label: "Is not set" },
];

const LINK_OPERATORS = [
  { value: "is_set", label: "Is set" },
  { value: "is_not_set", label: "Is not set" },
];

function getOperatorsForFieldType(fieldType: string) {
  switch (fieldType) {
    case "date":
    case "datetime":
      return DATE_OPERATORS;
    case "number":
    case "integer":
      return NUMBER_OPERATORS;
    case "url":
    case "link":
    case "panorama_file":
      return LINK_OPERATORS;
    default:
      return TEXT_OPERATORS;
  }
}

function isValuelessOperator(operator: string): boolean {
  return operator === "is_set" || operator === "is_not_set";
}

function getFieldTypeIcon(fieldType?: string): string {
  switch (fieldType) {
    case "number":
    case "integer":
      return "#";
    case "date":
    case "datetime":
      return "\u{1F4C5}";
    case "url":
    case "link":
    case "panorama_file":
      return "\u{1F517}";
    case "switch":
      return "\u{1F504}";
    case "longtext":
      return "\u{1F4DD}";
    default:
      return "T";
  }
}

const SORT_ORDERS = [
  { value: "asc", label: "Alphabetical (A\u2013Z)" },
  { value: "desc", label: "Alphabetical (Z\u2013A)" },
];

function getSortOrdersForFieldType(fieldType?: string) {
  switch (fieldType) {
    case "number":
    case "integer":
      return [
        { value: "asc", label: "Ascending (1\u20139)" },
        { value: "desc", label: "Descending (9\u20131)" },
      ];
    case "date":
    case "datetime":
      return [
        { value: "asc", label: "Oldest first" },
        { value: "desc", label: "Newest first" },
      ];
    default:
      return SORT_ORDERS;
  }
}

function getFilterSummary(filter: CollectionFilter, fields: FieldDef[]): string {
  const field = fields.find((f) => f.name === filter.fieldName);
  const displayName = field?.displayName || field?.name || filter.fieldName;
  const operators = getOperatorsForFieldType(field?.fieldType || "text");
  const opLabel = operators.find((o) => o.value === filter.operator)?.label || filter.operator;
  if (isValuelessOperator(filter.operator)) {
    return `${displayName} ${opLabel.toLowerCase()}`;
  }
  return `${displayName} ${opLabel.toLowerCase()} "${filter.value}"`;
}

function getSortSummary(sort: CollectionSort, fields: FieldDef[]): string {
  const field = fields.find((f) => f.name === sort.fieldName);
  const displayName = field?.displayName || field?.name || sort.fieldName;
  const orders = getSortOrdersForFieldType(field?.fieldType);
  const orderLabel = orders.find((o) => o.value === sort.order)?.label || sort.order;
  return `${displayName} ${orderLabel}`;
}

// --- Component ---

export function CollectionItemsPanel({
  config,
  fields,
  allItems,
  onChange,
  compact = false,
}: CollectionItemsPanelProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const hasDynamicSettings = config.filters.length > 0 || config.sort !== null;

  const handleModeChange = (newMode: "dynamic" | "selected") => {
    if (newMode === config.mode) return;

    // Clear filters and sort when switching to "Selected" mode
    if (newMode === "selected" && hasDynamicSettings) {
      onChange({
        ...config,
        mode: newMode,
        filters: [],
        sort: null,
      });
      return;
    }

    onChange({ ...config, mode: newMode });
  };


  // --- Filter management ---

  const addFilter = () => {
    const firstField = fields[0];
    if (!firstField) return;
    const operators = getOperatorsForFieldType(firstField.fieldType || "text");
    const newFilter: CollectionFilter = {
      id: crypto.randomUUID(),
      fieldName: firstField.name,
      operator: operators[0].value,
      value: "",
    };
    onChange({ ...config, filters: [...config.filters, newFilter] });
  };

  const updateFilter = (id: string, updates: Partial<CollectionFilter>) => {
    onChange({
      ...config,
      filters: config.filters.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    });
  };

  const removeFilter = (id: string) => {
    onChange({
      ...config,
      filters: config.filters.filter((f) => f.id !== id),
    });
  };

  const handleFilterFieldChange = (filterId: string, fieldName: string) => {
    const field = fields.find((f) => f.name === fieldName);
    const operators = getOperatorsForFieldType(field?.fieldType || "text");
    updateFilter(filterId, {
      fieldName,
      operator: operators[0].value,
      value: "",
    });
  };

  // --- Sort management ---

  const addSort = () => {
    const firstField = fields[0];
    if (!firstField) return;
    onChange({
      ...config,
      sort: { fieldName: firstField.name, order: "asc" },
    });
  };

  const removeSort = () => {
    onChange({ ...config, sort: null });
  };

  // --- Selected items ---

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems;
    const q = searchQuery.toLowerCase();
    return allItems.filter((item) => {
      const name = (item.name as string) || (item[Object.keys(item)[0]] as string) || "";
      return String(name).toLowerCase().includes(q);
    });
  }, [allItems, searchQuery]);

  const toggleItem = (itemId: string) => {
    const selected = new Set(config.selectedItemIds);
    if (selected.has(itemId)) {
      selected.delete(itemId);
    } else {
      selected.add(itemId);
    }
    onChange({ ...config, selectedItemIds: Array.from(selected) });
  };

  const deselectAll = () => {
    onChange({ ...config, selectedItemIds: [] });
  };

  const getItemId = (item: Record<string, unknown>): string => {
    return String(item.id ?? item._id ?? item.slug ?? item.name ?? JSON.stringify(item));
  };

  const getItemLabel = (item: Record<string, unknown>): string => {
    const name = item.name ?? item[Object.keys(item)[0]];
    return String(name || "Untitled");
  };

  return (
    <div className="space-y-0">
      {/* Items label and toggle - only show label in non-compact mode */}
      <div className="flex items-center gap-2">
        {!compact && <span className="text-xs text-gray-300">Items</span>}

        {/* Dynamic / Selected toggle */}
        <div className="flex bg-gray-700 rounded-md p-0.5 text-xs flex-1 gap-0.5">
          <button
            type="button"
            className={`flex-1 py-0.5 rounded transition-colors ${
              config.mode === "selected"
                ? "bg-gray-500 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            onClick={() => handleModeChange("selected")}
          >
            Selected
          </button>
          <button
            type="button"
            className={`flex-1 py-0.5 rounded transition-colors ${
              config.mode === "dynamic"
                ? "bg-gray-500 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            onClick={() => handleModeChange("dynamic")}
          >
            Dynamic
          </button>
        </div>
      </div>

      {/* Dynamic mode */}
      {config.mode === "dynamic" && (
        <div className="space-y-3">
          {/* Filters section */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-300">Filters</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-gray-400 hover:text-white hover:bg-gray-600"
                onClick={addFilter}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>

            {config.filters.length > 0 && (
              <div className="space-y-2 mt-1">
                {config.filters.map((filter) => {
                  const field = fields.find((f) => f.name === filter.fieldName);
                  const operators = getOperatorsForFieldType(field?.fieldType || "text");
                  const showValue = !isValuelessOperator(filter.operator);

                  return (
                    <div key={filter.id} className="bg-gray-700/50 rounded p-2 space-y-1.5">
                      {/* Filter summary pill */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-300 truncate flex-1">
                          {getFilterSummary(filter, fields)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 text-gray-500 hover:text-red-400 hover:bg-transparent flex-shrink-0"
                          onClick={() => removeFilter(filter.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>

                      {/* Field */}
                      <div className="space-y-0.5">
                        <Label className="text-xs text-gray-300">Field</Label>
                        <Select
                          value={filter.fieldName}
                          onValueChange={(val) => handleFilterFieldChange(filter.id, val)}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            {fields.map((f) => (
                              <SelectItem
                                key={f.name}
                                value={f.name}
                                className="text-white text-xs"
                              >
                                <span className="mr-1.5 opacity-60">
                                  {getFieldTypeIcon(f.fieldType)}
                                </span>
                                {f.displayName || f.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* When (operator) */}
                      <div className="space-y-0.5">
                        <Label className="text-xs text-gray-300">When</Label>
                        <Select
                          value={filter.operator}
                          onValueChange={(val) =>
                            updateFilter(filter.id, { operator: val, value: "" })
                          }
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            {operators.map((op) => (
                              <SelectItem
                                key={op.value}
                                value={op.value}
                                className="text-white text-xs"
                              >
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Value (only when operator requires it) */}
                      {showValue && (
                        <div className="space-y-0.5">
                          <Label className="text-xs text-gray-300">Value</Label>
                          <input
                            type={
                              field?.fieldType === "number" || field?.fieldType === "integer"
                                ? "number"
                                : field?.fieldType === "date" || field?.fieldType === "datetime"
                                  ? "date"
                                  : "text"
                            }
                            className="w-full h-7 px-2 text-xs bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                            placeholder="Enter value..."
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sort section */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-300">Sort</span>
              {!config.sort && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 text-gray-400 hover:text-white hover:bg-gray-600"
                  onClick={addSort}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              )}
            </div>

            {config.sort && (
              <div className="bg-gray-700/50 rounded p-2 space-y-1.5 mt-1">
                {/* Sort summary pill */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300 truncate flex-1">
                    {getSortSummary(config.sort, fields)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 text-gray-500 hover:text-red-400 hover:bg-transparent flex-shrink-0"
                    onClick={removeSort}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>

                {/* Field */}
                <div className="space-y-0.5">
                  <Label className="text-xs text-gray-300">Field</Label>
                  <Select
                    value={config.sort.fieldName}
                    onValueChange={(val) => {
                      const field = fields.find((f) => f.name === val);
                      const orders = getSortOrdersForFieldType(field?.fieldType);
                      onChange({
                        ...config,
                        sort: { fieldName: val, order: orders[0].value as "asc" | "desc" },
                      });
                    }}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {fields.map((f) => (
                        <SelectItem key={f.name} value={f.name} className="text-white text-xs">
                          <span className="mr-1.5 opacity-60">{getFieldTypeIcon(f.fieldType)}</span>
                          {f.displayName || f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Order */}
                <div className="space-y-0.5">
                  <Label className="text-xs text-gray-300">Order</Label>
                  <Select
                    value={config.sort.order}
                    onValueChange={(val) =>
                      onChange({
                        ...config,
                        sort: {
                          fieldName: config.sort?.fieldName ?? "",
                          order: val as "asc" | "desc",
                        },
                      })
                    }
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {(() => {
                        const field = fields.find((f) => f.name === config.sort?.fieldName);
                        return getSortOrdersForFieldType(field?.fieldType).map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-white text-xs">
                            {o.label}
                          </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected mode */}
      {config.mode === "selected" && (
        <div className="space-y-2">
          {/* Search + item list */}
          <div className="bg-gray-700/50 rounded overflow-hidden">
            {/* Search */}
            <div className="p-1.5">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500" />
                <input
                  type="text"
                  className="w-full h-7 pl-7 pr-2 text-xs bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  placeholder="Search items"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Item list */}
            <div className="max-h-48 overflow-y-auto">
              {filteredItems.map((item) => {
                const itemId = getItemId(item);
                const isSelected = config.selectedItemIds.includes(itemId);
                const label = getItemLabel(item);
                const isDraft = item.status === "draft" || item._status === "draft";

                return (
                  <label
                    key={itemId}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-600/50 cursor-pointer text-xs text-white"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleItem(itemId)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="truncate">
                      {isDraft && <span className="text-gray-500">[Draft] </span>}
                      {label}
                    </span>
                  </label>
                );
              })}
              {filteredItems.length === 0 && (
                <div className="px-2 py-3 text-xs text-gray-500 text-center">No items found</div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-600 text-xs text-gray-400">
              <span>{config.selectedItemIds.length} selected</span>
              <button
                type="button"
                className="hover:text-white transition-colors"
                onClick={deselectAll}
              >
                Deselect all
              </button>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}

// --- Utility: apply filters and sort to collection data ---

export function applyCollectionItemsConfig(
  data: Array<Record<string, unknown>>,
  config: CollectionItemsConfig | undefined,
  fields: Array<{ name: string; fieldType?: string }>,
): Array<Record<string, unknown>> {
  if (!config) return data;

  if (config.mode === "selected") {
    // In selected mode, return only selected items in selection order
    if (config.selectedItemIds.length === 0) return data;
    return config.selectedItemIds
      .map((id) =>
        data.find((item) => {
          const itemId = String(
            item.id ?? item._id ?? item.slug ?? item.name ?? JSON.stringify(item),
          );
          return itemId === id;
        }),
      )
      .filter(Boolean) as Array<Record<string, unknown>>;
  }

  // Dynamic mode: apply filters then sort
  let result = [...data];

  // Apply filters
  for (const filter of config.filters) {
    result = result.filter((item) => {
      const rawValue = item[filter.fieldName];

      switch (filter.operator) {
        case "is_set":
          return rawValue != null && rawValue !== "";
        case "is_not_set":
          return rawValue == null || rawValue === "";
        case "equals":
          return String(rawValue ?? "").toLowerCase() === filter.value.toLowerCase();
        case "does_not_equal":
          return String(rawValue ?? "").toLowerCase() !== filter.value.toLowerCase();
        case "contains":
          return String(rawValue ?? "")
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        case "does_not_contain":
          return !String(rawValue ?? "")
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        case "is_greater_than": {
          const numVal = Number(rawValue);
          const filterNum = Number(filter.value);
          return !Number.isNaN(numVal) && !Number.isNaN(filterNum) && numVal > filterNum;
        }
        case "is_less_than": {
          const numVal = Number(rawValue);
          const filterNum = Number(filter.value);
          return !Number.isNaN(numVal) && !Number.isNaN(filterNum) && numVal < filterNum;
        }
        case "is_after_or_equal": {
          if (!rawValue || !filter.value) return false;
          return new Date(String(rawValue)) >= new Date(filter.value);
        }
        case "is_before_or_equal": {
          if (!rawValue || !filter.value) return false;
          return new Date(String(rawValue)) <= new Date(filter.value);
        }
        default:
          return true;
      }
    });
  }

  // Apply sort
  if (config.sort) {
    const { fieldName, order } = config.sort;
    const field = fields.find((f) => f.name === fieldName);
    const isNumeric = field?.fieldType === "number" || field?.fieldType === "integer";
    const isDate = field?.fieldType === "date" || field?.fieldType === "datetime";
    const multiplier = order === "asc" ? 1 : -1;

    result.sort((a, b) => {
      const aVal = a[fieldName];
      const bVal = b[fieldName];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (isNumeric) {
        return (Number(aVal) - Number(bVal)) * multiplier;
      }
      if (isDate) {
        return (new Date(String(aVal)).getTime() - new Date(String(bVal)).getTime()) * multiplier;
      }
      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });
  }

  return result;
}
